import { useEffect, useState } from 'react';

import isNull from 'lodash/isNull';

import { getMediaLists } from '@/services/mediaManagement/index';
import { IMediaManagement } from '@/services/mediaManagement/interface';

const MEDIA_STORAGE_KEY = 'VISION_LINE_MEDIA_LIST';

// ========== 发布订阅：数据更新时通知所有订阅的组件 ==========
type Listener = () => void;
const listeners = new Set<Listener>();

function subscribeMediaUpdate(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function notifyMediaUpdate() {
  listeners.forEach((fn) => fn());
}

// ========== React Hook: 订阅媒体数据更新，数据变化时触发组件重新渲染 ==========
export function useMediaUpdate() {
  const [, setVersion] = useState(0);
  useEffect(() => {
    return subscribeMediaUpdate(() => setVersion((v) => v + 1));
  }, []);
}

// ========== SWR: Stale-While-Revalidate ==========
let pendingPromise: Promise<void> | null = null;

async function fetchAndUpdateCache(): Promise<void> {
  if (pendingPromise) return pendingPromise;

  pendingPromise = (async () => {
    try {
      const [res, error] = await getMediaLists();

      if (res) {
        const oldData = localStorage.getItem(MEDIA_STORAGE_KEY);
        // 按 media_key 排序后序列化，避免后端返回顺序不同导致误判为数据变化
        const sortedRes = [...res].sort((a, b) => a.media_key - b.media_key);
        const newData = JSON.stringify(sortedRes);
        const isFirstLoad = isNull(oldData) || oldData === '[]';

        // 将媒体数据存入 localStorage
        localStorage.setItem(MEDIA_STORAGE_KEY, newData);

        if (isFirstLoad) {
          // 首次加载：刷新页面确保所有组件获取到数据
          console.log('触发媒体空数据界面刷新');
          window.location.reload();
          return;
        }

        // 数据有变化时通知所有订阅者重新渲染
        if (oldData !== newData) {
          notifyMediaUpdate();
        }
      } else {
        console.error('拉取媒体数据失败', error);
      }
    } finally {
      pendingPromise = null;
    }
  })();

  return pendingPromise;
}

const scheduleIdleTask = (cb: () => void) => {
  if (typeof requestIdleCallback !== 'undefined') {
    requestIdleCallback(cb);
  } else {
    setTimeout(cb, 0);
  }
};

export const getMediaMap = async () => {
  const cachedData = localStorage.getItem(MEDIA_STORAGE_KEY);
  const hasCache = !isNull(cachedData) && cachedData !== '[]';

  if (hasCache) {
    // SWR：有缓存，立即返回，浏览器空闲时后台静默更新
    if (!pendingPromise) {
      scheduleIdleTask(() => fetchAndUpdateCache());
    }
    return;
  }

  // 无缓存（首次加载）：等待接口返回
  return fetchAndUpdateCache();
};

// ========== 用于访问媒体数据的 Store ==========
export const mediaStore = {
  get allMediaList() {
    const tempMediaList = localStorage.getItem(MEDIA_STORAGE_KEY);
    if (tempMediaList) {
      const tempParseList = JSON.parse(tempMediaList) as IMediaManagement.Common.MediaMetaInfo[];

      return tempParseList?.map((item) => ({
        value: item.media_key,
        title: item.media_name,
        ...item,
      }));
    } else {
      return [];
    }
  },
  get smallMediaList() {
    const tempMediaList = localStorage.getItem(MEDIA_STORAGE_KEY);
    if (tempMediaList) {
      const tempData = JSON.parse(tempMediaList) as IMediaManagement.Common.MediaMetaInfo[];

      return tempData
        .filter((item) => item.new_media === IMediaManagement.Common.MediaFlagEnum.YES)
        .map((item) => ({
          value: item.media_key,
          title: item.media_name,
          ...item,
        }));
    } else {
      return [];
    }
  },
  get allMediaMap() {
    const tempMediaList = localStorage.getItem(MEDIA_STORAGE_KEY);
    if (tempMediaList) {
      const tempData = JSON.parse(tempMediaList);

      return createMediaMap(tempData);
    } else {
      return {};
    }
  },
  get smallMediaMap() {
    const tempMediaList = localStorage.getItem(MEDIA_STORAGE_KEY);
    if (tempMediaList) {
      const tempData = JSON.parse(tempMediaList);
      const smallMediaList = tempData.filter((item: IMediaManagement.Common.MediaMetaInfo) => {
        return item.new_media === IMediaManagement.Common.MediaFlagEnum.YES;
      });
      return createMediaMap(smallMediaList);
    } else {
      return {};
    }
  },
  get mainMediaMap() {
    const tempMediaList = localStorage.getItem(MEDIA_STORAGE_KEY);
    if (tempMediaList) {
      const tempData = JSON.parse(tempMediaList);
      const mainMediaList = tempData.filter((item: IMediaManagement.Common.MediaMetaInfo) => {
        return item.new_media === IMediaManagement.Common.MediaFlagEnum.NO;
      });

      return createMediaMap(mainMediaList);
    } else {
      return {};
    }
  },
  get allMediaStringMap() {
    const tempMediaList = localStorage.getItem(MEDIA_STORAGE_KEY);
    if (tempMediaList) {
      const tempData = JSON.parse(tempMediaList);

      return createStringMap(tempData);
    } else {
      return {};
    }
  },
  get allMediaValueMap() {
    const tempMediaList = localStorage.getItem(MEDIA_STORAGE_KEY);
    if (tempMediaList) {
      const tempData = JSON.parse(tempMediaList);

      return createValueMap(tempData);
    } else {
      return {};
    }
  },
  get mainMediaValueMap() {
    const tempMediaList = localStorage.getItem(MEDIA_STORAGE_KEY);
    if (tempMediaList) {
      const tempData = JSON.parse(tempMediaList);
      const mainMediaList = tempData.filter((item: IMediaManagement.Common.MediaMetaInfo) => {
        return item.new_media === IMediaManagement.Common.MediaFlagEnum.NO;
      });
      return createValueMap(mainMediaList);
    } else {
      return {};
    }
  },
  get smallMediaValueMap() {
    const tempMediaList = localStorage.getItem(MEDIA_STORAGE_KEY);
    if (tempMediaList) {
      const tempData = JSON.parse(tempMediaList);
      const smallMediaList = tempData.filter((item: IMediaManagement.Common.MediaMetaInfo) => {
        return item.new_media === IMediaManagement.Common.MediaFlagEnum.YES;
      });
      return createValueMap(smallMediaList);
    } else {
      return {};
    }
  },
  get mediaAssetMap() {
    const tempMediaList = localStorage.getItem(MEDIA_STORAGE_KEY);
    if (tempMediaList) {
      const tempData = JSON.parse(tempMediaList);

      return createAssetMap(tempData);
    } else {
      return {};
    }
  },

  // 新增：获取支持开户的媒体列表
  get supportAccountOpenMediaList() {
    const tempMediaList = localStorage.getItem(MEDIA_STORAGE_KEY);
    if (tempMediaList) {
      const tempData = JSON.parse(tempMediaList);

      return tempData
        .filter(
          (item: IMediaManagement.Common.MediaMetaInfo) =>
            item.support_account_open === IMediaManagement.Common.MediaFlagEnum.YES,
        )
        .map((item: IMediaManagement.Common.MediaMetaInfo) => ({
          value: item.media_key,
          title: item.media_name,
          ...item,
        }));
    } else {
      return [];
    }
  },

  // 新增：获取支持开户的媒体映射
  get supportAccountOpenMediaMap() {
    const tempMediaList = localStorage.getItem(MEDIA_STORAGE_KEY);
    if (tempMediaList) {
      const tempData = JSON.parse(tempMediaList);
      const supportList = tempData.filter((item: IMediaManagement.Common.MediaMetaInfo) => {
        return item.support_account_open === IMediaManagement.Common.MediaFlagEnum.YES;
      });
      return createMediaMap(supportList);
    } else {
      return {};
    }
  },

  // 新增：获取支持开户的小媒体列表
  get supportAccountOpenSmallMediaList() {
    const tempMediaList = localStorage.getItem(MEDIA_STORAGE_KEY);
    if (tempMediaList) {
      const tempData = JSON.parse(tempMediaList);

      return tempData
        .filter(
          (item: IMediaManagement.Common.MediaMetaInfo) =>
            item.support_account_open === IMediaManagement.Common.MediaFlagEnum.YES &&
            item.new_media === IMediaManagement.Common.MediaFlagEnum.YES,
        )
        .map((item: IMediaManagement.Common.MediaMetaInfo) => ({
          value: item.media_key,
          title: item.media_name,
          ...item,
        }));
    } else {
      return [];
    }
  },

  // 新增：获取支持开户的主媒体列表
  get supportAccountOpenMainMediaList() {
    const tempMediaList = localStorage.getItem(MEDIA_STORAGE_KEY);
    if (tempMediaList) {
      const tempData = JSON.parse(tempMediaList);

      return tempData
        .filter(
          (item: IMediaManagement.Common.MediaMetaInfo) =>
            item.support_account_open === IMediaManagement.Common.MediaFlagEnum.YES &&
            item.new_media === IMediaManagement.Common.MediaFlagEnum.NO,
        )
        .map((item: IMediaManagement.Common.MediaMetaInfo) => ({
          value: item.media_key,
          title: item.media_name,
          ...item,
        }));
    } else {
      return [];
    }
  },

  // 新增：获取支持开户的小媒体映射
  get supportAccountOpenSmallMediaMap() {
    const tempMediaList = localStorage.getItem(MEDIA_STORAGE_KEY);
    if (tempMediaList) {
      const tempData = JSON.parse(tempMediaList);
      const supportSmallMediaList = tempData.filter(
        (item: IMediaManagement.Common.MediaMetaInfo) => {
          return (
            item.support_account_open === IMediaManagement.Common.MediaFlagEnum.YES &&
            item.new_media === IMediaManagement.Common.MediaFlagEnum.YES
          );
        },
      );
      return createMediaMap(supportSmallMediaList);
    } else {
      return {};
    }
  },

  // 新增：获取支持开户的主媒体映射
  get supportAccountOpenMainMediaMap() {
    const tempMediaList = localStorage.getItem(MEDIA_STORAGE_KEY);
    if (tempMediaList) {
      const tempData = JSON.parse(tempMediaList);
      const supportMainMediaList = tempData.filter(
        (item: IMediaManagement.Common.MediaMetaInfo) => {
          return (
            item.support_account_open === IMediaManagement.Common.MediaFlagEnum.YES &&
            item.new_media === IMediaManagement.Common.MediaFlagEnum.NO
          );
        },
      );
      return createMediaMap(supportMainMediaList);
    } else {
      return {};
    }
  },
};

// 工具函数：创建映射表
function createMediaMap(list: IMediaManagement.Common.MediaMetaInfo[]) {
  return list.reduce((acc, cur) => {
    acc[cur.media_key] = cur.media_name;
    return acc;
  }, {} as Record<number, string>);
}

function createStringMap(list: IMediaManagement.Common.MediaMetaInfo[]) {
  return list.reduce((acc, cur) => {
    acc[cur.media_key_value] = cur.media_name;
    return acc;
  }, {} as Record<string, string>);
}

function createValueMap(list: IMediaManagement.Common.MediaMetaInfo[]) {
  return list.reduce((acc, cur) => {
    acc[cur.media_key_value] = cur.media_key;
    return acc;
  }, {} as Record<string, number>);
}

function createAssetMap(list: IMediaManagement.Common.MediaMetaInfo[]) {
  return list.reduce((acc, cur) => {
    if (!cur.media_asset_name) return acc;
    acc[cur.media_key] = cur.media_asset_name;
    acc[cur.media_key_value] = cur.media_asset_name;
    acc[cur.media_name] = cur.media_asset_name;
    acc[cur.media_name_en] = cur.media_asset_name;
    return acc;
  }, {} as Record<string, string[]>);
}

export const findMediaByKey = (key: number, isNewMedia?: boolean) => {
  return (isNewMedia ? mediaStore.smallMediaList : mediaStore.allMediaList)?.find?.(
    (item) => item.media_key === key,
  );
};

// 按 media_key 取媒体展示名（英文环境取英文名），用于税费提示等需按媒体动态展示文案的场景
export const getMediaDisplayName = (key?: number, isEn?: boolean) => {
  if (key === undefined || key === null) return '';
  const media = findMediaByKey(key);
  return (isEn ? media?.media_name_en : media?.media_name) ?? '';
};

export const findMediaByKeys = (names: string[], isNewMedia?: boolean) => {
  return (isNewMedia ? mediaStore.smallMediaList : mediaStore.allMediaList)?.filter?.((item) =>
    names.includes(item.media_key_value),
  );
};
